import cn from 'classnames';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Checkbox, EmptyState, LoadingSkeleton } from 'sonata-react-components';

import { SortHeader } from './SortHeader';
import {
  type EntityTableFetchData,
  type EntityTableItem,
  type SortDirection,
  useEntityTableData
} from './useEntityTableData';
import { useNameColumnResize } from './useNameColumnResize';

import type { AnimationEvent, CSSProperties, MouseEvent, ReactNode } from 'react';

import './entityTable.scss';

export type EntityTableColumn<T> = {
  /** Aligns the header and the cells of the column; left when omitted. */
  align?: 'right';
  header: string;
  id: string;
  render: (item: T) => ReactNode;
  sortable?: boolean;
  /** Any CSS width (px or a `var(--list-col-x)`); the table scrolls horizontally when they don't fit. */
  width: string;
};

type Props<T extends EntityTableItem> = {
  actionsWidth: string;
  /** BEM base of the list (e.g. `lineItemsList`): rows keep `{list}__tableListRowsListRow` for the list SCSS. */
  className: string;
  /** Scrolling columns, already filtered by visibility. */
  columns: EntityTableColumn<T>[];
  defaultSelectedItems?: T[];
  emptyStateTitle?: string;
  /** Rows expand on click when the item has `expandableContent`. */
  expandOnRowClick?: boolean;
  fetchData: EntityTableFetchData<T>;
  id: string;
  initialPageSize?: number;
  /** Pinned left, after the selection checkbox; user-resizable down to NAME_MIN_WIDTH. */
  nameColumn: Omit<EntityTableColumn<T>, 'id' | 'width'>;
  onSelectionChange?: (items: T[]) => void;
  onSortChange?: (columnId: string, direction: SortDirection) => void;
  /** Pinned right. */
  renderActions: (item: T) => ReactNode;
  /** Decoration of the pinned leading cell (e.g. the pacing status strip). */
  renderLeading?: (item: T) => ReactNode;
};

const LEADING_WIDTH = '40px';
// Same floor as DeviatedLineItemsList's name column, so every list stops shrinking it at 150px.
const NAME_MIN_WIDTH = 150;
const SKELETON_ROWS = 5;
// Clicks on these run their own action and must not also expand the row.
const INNER_CONTROL_SELECTOR = 'a, button, input, label, select, textarea, [role="button"], [data-row-action]';

/**
 * Clicks that must not toggle the row: portaled content (menus, tooltips) bubbles through React
 * but happens outside the row DOM, and inner controls run their own action.
 */
const isIgnoredRowClick = (event: MouseEvent<HTMLTableRowElement>) => {
  const target = event.target as HTMLElement | null;
  return !target || !event.currentTarget.contains(target) || Boolean(target.closest(INNER_CONTROL_SELECTOR));
};

/**
 * Entity list rendered as a <table>: selection + name pinned left, actions pinned right, the rest of
 * the columns scroll horizontally. Expanded content spans the visible width and does not scroll.
 */
export function EntityTable<T extends EntityTableItem>({
  actionsWidth,
  className,
  columns,
  defaultSelectedItems,
  emptyStateTitle,
  expandOnRowClick = false,
  fetchData,
  id,
  initialPageSize = 15,
  nameColumn,
  onSelectionChange,
  onSortChange,
  renderActions,
  renderLeading
}: Readonly<Props<T>>) {
  const { formatMessage } = useIntl();
  const table = useEntityTableData<T>({
    defaultSelectedItems,
    fetchData,
    initialPageSize,
    onSelectionChange,
    onSortChange
  });
  const { expandedIds, isFetching, isInitialLoading, items, pagination, selectedItems } = table;
  // Expanded rows playing their closing animation; they are collapsed once it ends
  const [collapsingIds, setCollapsingIds] = useState<Set<string>>(new Set());

  const setCollapsing = (itemId: string, collapsing: boolean) =>
    setCollapsingIds(previous => {
      const next = new Set(previous);
      if (collapsing) {
        next.add(itemId);
      } else {
        next.delete(itemId);
      }
      return next;
    });

  const handleExpandToggle = (itemId: string) => {
    if (collapsingIds.has(itemId)) {
      setCollapsing(itemId, false);
    } else if (expandedIds.has(itemId) && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setCollapsing(itemId, true);
    } else {
      table.toggleExpand(itemId);
    }
  };

  const handleCollapseEnd = (itemId: string) => (event: AnimationEvent<HTMLDivElement>) => {
    // Ignores the animations of the expanded content itself (e.g. loading skeletons)
    if (event.target === event.currentTarget && event.animationName === 'entityTableCollapse') {
      setCollapsing(itemId, false);
      table.toggleExpand(itemId);
    }
  };

  const sentinelRef = useRef<HTMLTableRowElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { loadMore } = table;
  const nameResize = useNameColumnResize(scrollRef, NAME_MIN_WIDTH);
  // Once the name has a user-defined width, a filler column right after it takes the free space, so
  // the other columns stay aligned to the right instead of following the name's edge.
  const hasFiller = nameResize.width !== null;
  const columnCount = columns.length + 3 + (hasFiller ? 1 : 0);
  const resizeHandle = (
    <div aria-hidden className="entityTable__resizeHandle" data-row-action {...nameResize.handleProps} />
  );
  const filler = hasFiller ? <td aria-hidden className="entityTable__filler" /> : null;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !pagination.hasMore) {
      return;
    }
    const observer = new IntersectionObserver(entries => entries[0]?.isIntersecting && loadMore(), {
      threshold: 0.75
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore, pagination.hasMore, items.length]);

  const tableStyle = {
    '--entity-table-leading-width': LEADING_WIDTH,
    minWidth: `calc(${[LEADING_WIDTH, `${nameResize.width ?? NAME_MIN_WIDTH}px`, ...columns.map(column => column.width), actionsWidth].join(' + ')})`
  } as CSSProperties;

  const selectedCount = selectedItems.length;
  const isSelected = (item: T) => selectedItems.some(selected => selected.id === item.id);

  const renderBody = () => {
    if (isInitialLoading) {
      return Array.from({ length: SKELETON_ROWS }, (_, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder rows
        <tr className="entityTable__skeletonRow" key={index}>
          <td colSpan={columnCount}>
            <LoadingSkeleton height={48} />
          </td>
        </tr>
      ));
    }
    if (items.length === 0) {
      return (
        <tr>
          <td className="entityTable__emptyState" colSpan={columnCount}>
            <EmptyState
              description=""
              fontSizes={{ icon: '24px' }}
              icon="fa-solid fa-empty-set"
              id={`taptapList-rowsList-emptyState-${id}`}
              show
              title={emptyStateTitle ?? ''}
            />
          </td>
        </tr>
      );
    }
    return items.map(item => {
      const expanded = expandedIds.has(item.id);
      const selected = isSelected(item);
      const canExpand = expandOnRowClick && Boolean(item.expandableContent);
      return (
        <Fragment key={item.id}>
          <tr aria-hidden className="entityTable__spacer">
            <td colSpan={columnCount} />
          </tr>
          <tr
            className={cn('entityTable__row', `${className}__tableListRowsListRow`, {
              'entityTable__row--clickable': canExpand,
              'entityTable__row--expanded': expanded,
              'entityTable__row--selected': selected
            })}
            id={`taptapList-row-${id}-${item.id}`}
            onClick={event => canExpand && !isIgnoredRowClick(event) && handleExpandToggle(item.id)}
          >
            <td className="entityTable__leading">
              {renderLeading?.(item)}
              <Checkbox
                active={selected}
                id={`taptapList-row-checkbox-${id}-${item.id}`}
                onClickAction={(_: string, checked: boolean) => table.toggleSelection(item, checked)}
              />
            </td>
            <td className="entityTable__name">
              <div className="entityTable__nameContent">{nameColumn.render(item)}</div>
              {resizeHandle}
            </td>
            {filler}
            {columns.map(column => (
              <td className={cn({ 'entityTable__cell--alignRight': column.align === 'right' })} key={column.id}>
                {column.render(item)}
              </td>
            ))}
            <td className="entityTable__actions">{renderActions(item)}</td>
          </tr>
          {expanded && item.expandableContent ? (
            <tr
              className={cn('entityTable__expandedRow', {
                'entityTable__expandedRow--collapsing': collapsingIds.has(item.id),
                'entityTable__expandedRow--selected': selected
              })}
            >
              <td colSpan={columnCount}>
                <div className="entityTable__expandedContent" onAnimationEnd={handleCollapseEnd(item.id)}>
                  {/* Repeats the leading decoration so the pacing strip spans the whole expanded row; the
                      content starts right after the strip instead of after the whole leading column. */}
                  {renderLeading?.(item)}
                  <div className="entityTable__expandedInner">{item.expandableContent()}</div>
                </div>
              </td>
            </tr>
          ) : null}
        </Fragment>
      );
    });
  };

  return (
    <div className="entityTable">
      <div className="entityTable__scroll" ref={scrollRef}>
        <table className="entityTable__table" style={tableStyle}>
          <colgroup>
            <col style={{ width: LEADING_WIDTH }} />
            <col style={hasFiller ? { width: `${nameResize.width}px` } : undefined} />
            {hasFiller ? <col /> : null}
            {columns.map(column => (
              <col key={column.id} style={{ width: column.width }} />
            ))}
            <col style={{ width: actionsWidth }} />
          </colgroup>
          <thead>
            <tr>
              <th className="entityTable__leading">
                <Checkbox
                  active={selectedCount > 0}
                  disabled={isFetching}
                  id={`toolbar-checkbox_${id}`}
                  onClickAction={table.toggleAllSelections}
                  type={selectedCount !== items.length ? 'partial' : 'checkbox'}
                />
              </th>
              <th className="entityTable__name">
                <SortHeader
                  columnId="name"
                  label={nameColumn.header}
                  onSort={table.handleSort}
                  sortState={table.sortState}
                  sortable={nameColumn.sortable}
                />
                {resizeHandle}
              </th>
              {hasFiller ? <th aria-hidden className="entityTable__filler" /> : null}
              {columns.map(column => (
                <th className={cn({ 'entityTable__cell--alignRight': column.align === 'right' })} key={column.id}>
                  <SortHeader
                    columnId={column.id}
                    label={column.header}
                    onSort={table.handleSort}
                    sortState={table.sortState}
                    sortable={column.sortable}
                  />
                </th>
              ))}
              <th className="entityTable__actions" />
            </tr>
          </thead>
          <tbody>
            {renderBody()}
            {!isInitialLoading && items.length > 0 && pagination.hasMore ? (
              <tr className="entityTable__loadingMore" ref={sentinelRef}>
                <td colSpan={columnCount}>
                  {isFetching ? (
                    <>
                      <i className="fa fa-spinner fa-spin" />{' '}
                      {formatMessage({ defaultMessage: 'Loading more...', id: 'taptapCoreList.loadingMore' })}
                    </>
                  ) : (
                    formatMessage({ defaultMessage: 'Scroll to load more', id: 'taptapCoreList.scrollToLoadMore' })
                  )}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
